import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

const AdminProductEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Editable fields
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [basePrice, setBasePrice] = useState('');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);

  const categories = ['Hats', 'Hoodies', 'Sweaters', 'Tees', 'Polos', 'Pants', 'Shorts', 'Outerwear', 'Womens', 'Youth'];

  // Get unique colors from variants
  const getUniqueColors = () => {
    if (!product || !product.variants) return [];
    const colors = [...new Set(product.variants.map(v => v.color))];
    return colors.sort();
  };

  // Get the current image assigned to a color
  const getColorImage = (color) => {
    if (!product || !product.variants) return null;
    const variant = product.variants.find(v => v.color === color);
    return variant ? variant.image_url : null;
  };

  useEffect(() => {
    loadProduct();
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await api.getProduct(id);
      setProduct(data);
      setDescription(data.description || '');
      setCategory(data.category || 'Tees');
      setBasePrice(data.base_price ? data.base_price.toString() : '');
      setBgColor(data.bg_color || '#FFFFFF');
    } catch (err) {
      setError('FAILED TO LOAD PRODUCT');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    
    // Create preview URLs
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);
  };

  const handleUploadImages = async () => {
    if (selectedFiles.length === 0) {
      setError('NO FILES SELECTED');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Convert files to base64 data URLs
      const imagePromises = selectedFiles.map(file => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      
      const base64Images = await Promise.all(imagePromises);
      await api.adminUploadProductImages(id, base64Images);
      
      setSuccess('IMAGES UPLOADED SUCCESSFULLY');
      
      // Clean up preview URLs
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      setSelectedFiles([]);
      setPreviewUrls([]);
      
      // Reload product to show updated data
      await loadProduct();
      
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      setError('FAILED TO UPLOAD IMAGES');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      // Update description, category, price, and bg_color
      await api.adminUpdateProduct(id, {
        ...product,
        description,
        category,
        base_price: parseFloat(basePrice),
        bg_color: bgColor
      });
      
      setSuccess('PRODUCT UPDATED SUCCESSFULLY');
      
      // Reload product
      await loadProduct();
      
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      setError('FAILED TO SAVE CHANGES');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = async (imageUrl) => {
    if (!window.confirm('Delete this image permanently? This will remove it from all variants. This action cannot be undone.')) {
      return;
    }
    
    setError('');
    setSuccess('');
    
    try {
      await api.adminRemoveProductImage(id, imageUrl);
      setSuccess('IMAGE DELETED');
      await loadProduct();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(`FAILED TO DELETE IMAGE: ${err?.message || err}`);
      console.error(err);
    }
  };

  const handleAssignImageToColor = async (color, imageUrl) => {
    setError('');
    setSuccess('');

    try {
      await api.adminAssignVariantImage(id, color, imageUrl);
      setSuccess(`IMAGE ASSIGNED TO ${color.toUpperCase()}`);
      await loadProduct();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(`FAILED TO ASSIGN IMAGE: ${err?.message || err}`);
      console.error(err);
    }
  };

  // Promote an image to the front of the `images` array. The frontend
  // (Shop card + ProductDetail hero) reads `product.images[0]` as the
  // default display image, so this controls the "main" picture.
  const handleSetMainImage = async (imageUrl) => {
    setError('');
    setSuccess('');
    try {
      await api.adminSetMainImage(id, imageUrl);
      setSuccess('MAIN DISPLAY IMAGE UPDATED');
      await loadProduct();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(`FAILED TO SET MAIN IMAGE: ${err?.message || err}`);
      console.error(err);
    }
  };

  // Tag an image with (color, angle). angle = "front" | "back" | "extra" | "none".
  // Used by the storefront to render the front/back hover-flip view on
  // Product Detail and the Shop grid.
  const handleTagImage = async (imageUrl, color, angle) => {
    if (!color) return;
    setError('');
    setSuccess('');
    try {
      await api.adminTagImage(id, imageUrl, color, angle);
      setSuccess(angle === 'none'
        ? `UNTAGGED ${color.toUpperCase()}`
        : `TAGGED AS ${color.toUpperCase()} / ${angle.toUpperCase()}`);
      await loadProduct();
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      setError(`FAILED TO TAG IMAGE: ${err?.message || err}`);
      console.error(err);
    }
  };

  // Precompute two maps that used to be recomputed via .filter() inside the
  // render loop for EVERY image card:
  //   - colorToImage: color name -> its variant image URL (O(1) lookup)
  //   - colorsByImage: image URL  -> array of color names that use it
  // With ~20 colors × ~20 images the previous nested-filter approach ran
  // ~800 comparisons per render; this precomputes it once per data change.
  //
  // MUST be declared before any early return so hooks run in the same order
  // on every render (React Rules of Hooks).
  const colorToImage = useMemo(() => {
    const m = new Map();
    if (product && product.variants) {
      for (const v of product.variants) {
        if (v.color && !m.has(v.color)) m.set(v.color, v.image_url || null);
      }
    }
    return m;
  }, [product]);

  const colorsByImage = useMemo(() => {
    const m = new Map();
    for (const [color, url] of colorToImage.entries()) {
      if (!url) continue;
      const arr = m.get(url) || [];
      arr.push(color);
      m.set(url, arr);
    }
    return m;
  }, [colorToImage]);

  // Map: imageUrl -> array of { color, angle } tags for the FRONT/BACK/EXTRA system.
  // Rendered as pills on each image card so the admin sees exactly what's tagged.
  const tagsByImage = useMemo(() => {
    const m = new Map();
    const tags = (product && product.image_tags) || [];
    for (const t of tags) {
      if (!t || !t.url) continue;
      const arr = m.get(t.url) || [];
      arr.push({ color: t.color, angle: t.angle });
      m.set(t.url, arr);
    }
    return m;
  }, [product]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#FEFEE3', padding: '20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p style={{ fontSize: '14px', color: '#4C956C', textAlign: 'center' }}>LOADING...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ minHeight: '100vh', background: '#FEFEE3', padding: '20px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p style={{ fontSize: '14px', color: '#FF5A5F', textAlign: 'center' }}>{error || 'PRODUCT NOT FOUND'}</p>
          <button
            onClick={() => navigate('/admin')}
            className="retro-btn"
            style={{ background: '#2C6E49', color: '#FEFEE3', padding: '12px 24px', fontSize: '12px', margin: '20px auto', display: 'block' }}
          >
            {'<<< BACK TO ADMIN'}
          </button>
        </div>
      </div>
    );
  }

  const uniqueColors = getUniqueColors();

  return (
    <div style={{ minHeight: '100vh', background: '#FEFEE3' }}>
      {/* Header */}
      <div style={{ background: '#FEFEE3', padding: '20px', borderBottom: '4px solid #000', boxShadow: '0 4px 0 0 #000' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/admin')}
            className="retro-btn"
            style={{ background: '#FFF', border: '3px solid #000', color: '#2C2C2C', padding: '12px 20px', fontSize: '12px', boxShadow: '4px 4px 0 0 #000' }}
          >
            {'<<< BACK'}
          </button>
          
          <h1 style={{ fontSize: '20px', color: '#2C2C2C', letterSpacing: '0.12em' }}>[ PRODUCT EDITOR ]</h1>
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="retro-btn"
            style={{ 
              background: saving ? '#CCC' : '#2C6E49', 
              color: '#FEFEE3', 
              padding: '12px 24px', 
              fontSize: '12px',
              border: '3px solid #000',
              boxShadow: '4px 4px 0 0 #000',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'SAVING...' : 'SAVE >>>'}
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}>
        
        {/* Status Messages */}
        {error && (
          <div style={{ background: '#FF5A5F', color: '#FFF', padding: '15px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
            <p style={{ fontSize: '12px', fontWeight: 'bold', margin: 0 }}>{error}</p>
          </div>
        )}
        
        {success && (
          <div style={{ background: '#4AE54A', color: '#000', padding: '15px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
            <p style={{ fontSize: '12px', fontWeight: 'bold', margin: 0 }}>{success}</p>
          </div>
        )}
        
        {/* Product Info */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <h2 style={{ fontSize: '16px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em' }}>[ PRODUCT INFO ]</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
            <div>
              <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>NAME (READ-ONLY)</p>
              <p style={{ fontSize: '14px', color: '#2C2C2C', fontWeight: 'bold' }}>{product.name}</p>
            </div>
            <div>
              <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '8px' }}>CATEGORY</p>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '13px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  border: '3px solid #000',
                  boxShadow: '3px 3px 0 0 #000',
                  background: '#FEFEE3',
                  color: '#2C2C2C'
                }}
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat.toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div>
              <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '8px' }}>BASE PRICE</p>
              <input
                type="number"
                step="0.01"
                value={basePrice}
                onChange={(e) => setBasePrice(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '13px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  border: '3px solid #000',
                  boxShadow: '3px 3px 0 0 #000',
                  background: '#FEFEE3',
                  color: '#2C2C2C'
                }}
                placeholder="49.99"
              />
            </div>
            <div>
              <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '5px' }}>VARIANTS (READ-ONLY)</p>
              <p style={{ fontSize: '14px', color: '#2C2C2C' }}>{product.variants?.length || 0} colors</p>
            </div>
          </div>
        </div>
        
        {/* Description Editor */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <label htmlFor="description-input" style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '10px', display: 'block', letterSpacing: '0.12em', fontWeight: 'bold' }}>
            [ DESCRIPTION ]
          </label>
          <textarea
            id="description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: '13px',
              fontFamily: 'IBM Plex Mono, monospace',
              border: '3px solid #000',
              boxShadow: '3px 3px 0 0 #000',
              background: '#FEFEE3',
              color: '#2C2C2C',
              resize: 'vertical'
            }}
            placeholder="Enter product description..."
          />
        </div>

        {/* Background Color Editor */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <h2 style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em', fontWeight: 'bold' }}>[ BACKGROUND COLOR / HUE ]</h2>
          
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Color Picker */}
            <div>
              <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '8px' }}>PICK COLOR</p>
              <input
                type="color"
                value={bgColor}
                onChange={(e) => setBgColor(e.target.value)}
                style={{
                  width: '80px',
                  height: '40px',
                  border: '3px solid #000',
                  boxShadow: '3px 3px 0 0 #000',
                  cursor: 'pointer'
                }}
              />
            </div>

            {/* Hex Input */}
            <div style={{ flex: 1, minWidth: '200px' }}>
              <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '8px' }}>HEX CODE</p>
              <input
                type="text"
                value={bgColor}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '' || /^#[0-9A-Fa-f]{0,6}$/.test(value)) {
                    setBgColor(value);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '8px',
                  fontSize: '13px',
                  fontFamily: 'IBM Plex Mono, monospace',
                  border: '3px solid #000',
                  boxShadow: '3px 3px 0 0 #000',
                  background: '#FEFEE3',
                  color: '#2C2C2C'
                }}
                placeholder="#FFFFFF"
              />
            </div>

            {/* Preview */}
            <div>
              <p style={{ fontSize: '10px', color: '#4C956C', marginBottom: '8px' }}>PREVIEW</p>
              <div style={{
                width: '100px',
                height: '40px',
                background: bgColor,
                border: '3px solid #000',
                boxShadow: '3px 3px 0 0 #000'
              }} />
            </div>
          </div>
        </div>
        
        {/* Image Upload Section */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <h2 style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em', fontWeight: 'bold' }}>[ UPLOAD NEW IMAGES ]</h2>
          
          <div style={{ marginBottom: '15px' }}>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
              id="file-upload-input"
            />
            <label
              htmlFor="file-upload-input"
              className="retro-btn"
              style={{
                background: '#2C6E49',
                color: '#FEFEE3',
                padding: '12px 24px',
                fontSize: '12px',
                border: '3px solid #000',
                boxShadow: '4px 4px 0 0 #000',
                cursor: 'pointer',
                display: 'inline-block',
                fontWeight: 'bold'
              }}
            >
              📁 CHOOSE FILES
            </label>
            
            {selectedFiles.length > 0 && (
              <span style={{ marginLeft: '15px', fontSize: '12px', color: '#4C956C' }}>
                {selectedFiles.length} file(s) selected
              </span>
            )}
          </div>

          {/* Preview Selected Images */}
          {previewUrls.length > 0 && (
            <div>
              <p style={{ fontSize: '11px', color: '#4C956C', marginBottom: '10px', fontWeight: 'bold' }}>PREVIEW:</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px', marginBottom: '15px' }}>
                {previewUrls.map((url, index) => (
                  <div key={index} style={{ position: 'relative' }}>
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      style={{
                        width: '100%',
                        height: '120px',
                        objectFit: 'cover',
                        border: '3px solid #000',
                        boxShadow: '3px 3px 0 0 #000'
                      }}
                    />
                  </div>
                ))}
              </div>
              
              <button
                onClick={handleUploadImages}
                disabled={saving}
                className="retro-btn"
                style={{
                  background: saving ? '#CCC' : '#4AE54A',
                  color: '#000',
                  padding: '12px 24px',
                  fontSize: '12px',
                  border: '3px solid #000',
                  boxShadow: '4px 4px 0 0 #000',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {saving ? 'UPLOADING...' : '⬆ UPLOAD IMAGES'}
              </button>
            </div>
          )}
        </div>

        {/* Existing Images - Manage & Assign to Colors */}
        <div style={{ background: '#FFF', padding: '20px', marginBottom: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <h2 style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em', fontWeight: 'bold' }}>
            [ MANAGE IMAGES ({product.images?.length || 0}) ]
          </h2>
          
          {(!product.images || product.images.length === 0) ? (
            <p style={{ fontSize: '12px', color: '#999', textAlign: 'center', padding: '20px' }}>
              NO IMAGES UPLOADED YET
            </p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
              {product.images.map((imageUrl, index) => {
                const isMain = index === 0;
                return (
                <div
                  key={index}
                  style={{
                    background: '#FEFEE3',
                    border: isMain ? '4px solid #FFC857' : '3px solid #000',
                    boxShadow: isMain ? '4px 4px 0 0 #FFC857' : '3px 3px 0 0 #000',
                    padding: '10px',
                    position: 'relative',
                  }}
                >
                  {/* MAIN badge in the top-left corner of the picture */}
                  {isMain && (
                    <div
                      data-testid={`main-image-badge-${index}`}
                      style={{
                        position: 'absolute',
                        top: '14px',
                        left: '14px',
                        background: '#FFC857',
                        color: '#000',
                        padding: '4px 8px',
                        fontSize: '9px',
                        fontWeight: 'bold',
                        letterSpacing: '0.1em',
                        border: '2px solid #000',
                        boxShadow: '2px 2px 0 0 #000',
                        zIndex: 2,
                      }}
                    >
                      ★ MAIN
                    </div>
                  )}

                  {/* Image Preview */}
                  <img
                    src={imageUrl}
                    alt={`Product ${index + 1}`}
                    style={{
                      width: '100%',
                      height: '150px',
                      objectFit: 'cover',
                      border: '2px solid #000',
                      marginBottom: '10px'
                    }}
                  />

                  {/* SET AS MAIN button — only shows when this isn't already main */}
                  <button
                    onClick={() => !isMain && handleSetMainImage(imageUrl)}
                    disabled={isMain}
                    data-testid={`set-main-image-btn-${index}`}
                    className="retro-btn"
                    style={{
                      background: isMain ? '#FFC857' : '#000',
                      color: isMain ? '#000' : '#FFC857',
                      padding: '8px 12px',
                      fontSize: '10px',
                      border: '2px solid #000',
                      width: '100%',
                      marginBottom: '8px',
                      fontWeight: 'bold',
                      cursor: isMain ? 'default' : 'pointer',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {isMain ? '★ MAIN DISPLAY' : '☆ SET AS MAIN'}
                  </button>

                  {/* Delete Button */}
                  <button
                    onClick={() => handleRemoveImage(imageUrl)}
                    className="retro-btn"
                    style={{
                      background: '#FF5A5F',
                      color: '#FFF',
                      padding: '8px 12px',
                      fontSize: '10px',
                      border: '2px solid #000',
                      width: '100%',
                      marginBottom: '10px',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    🗑️ DELETE
                  </button>
                  
                  {/* Assign to Color Dropdown */}
                  <div>
                    <p style={{ fontSize: '9px', color: '#4C956C', marginBottom: '5px', fontWeight: 'bold' }}>ASSIGN TO COLOR:</p>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          handleAssignImageToColor(e.target.value, imageUrl);
                          e.target.value = ''; // Reset select
                        }
                      }}
                      style={{
                        width: '100%',
                        padding: '6px',
                        fontSize: '10px',
                        fontFamily: 'IBM Plex Mono, monospace',
                        border: '2px solid #000',
                        background: '#FFF',
                        color: '#2C2C2C',
                        cursor: 'pointer'
                      }}
                      defaultValue=""
                    >
                      <option value="">Select color...</option>
                      {uniqueColors.map(color => (
                        <option key={color} value={color}>
                          {color.toUpperCase()} {colorToImage.get(color) === imageUrl ? '✓' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Show which colors use this image — reads from the
                      precomputed colorsByImage map instead of running two
                      nested .filter() passes per render. */}
                  {(() => {
                    const usedBy = colorsByImage.get(imageUrl) || [];
                    if (usedBy.length === 0) return null;
                    return (
                      <div style={{ marginTop: '8px', padding: '6px', background: '#4AE54A', border: '2px solid #000' }}>
                        <p style={{ fontSize: '9px', color: '#000', margin: 0, fontWeight: 'bold' }}>
                          USED BY: {usedBy.join(', ').toUpperCase()}
                        </p>
                      </div>
                    );
                  })()}

                  {/* FRONT / BACK / EXTRA tagging — enables the hover-flip
                      front/back view on the storefront. Pick a color, then
                      pick FRONT or BACK. */}
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '2px dashed #999' }}>
                    <p style={{ fontSize: '9px', color: '#2C6E49', marginBottom: '5px', fontWeight: 'bold', letterSpacing: '0.08em' }}>
                      TAG AS FRONT/BACK:
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px' }}>
                      <select
                        data-testid={`image-tag-color-${index}`}
                        onChange={(e) => {
                          const color = e.target.value;
                          const angleSel = document.querySelector(`[data-testid="image-tag-angle-${index}"]`);
                          const angle = angleSel ? angleSel.value : '';
                          if (color && angle) {
                            handleTagImage(imageUrl, color, angle);
                            e.target.value = '';
                            if (angleSel) angleSel.value = '';
                          }
                        }}
                        style={{
                          padding: '5px',
                          fontSize: '9px',
                          fontFamily: 'IBM Plex Mono, monospace',
                          border: '2px solid #000',
                          background: '#FFF',
                          color: '#2C2C2C',
                          cursor: 'pointer',
                          minWidth: 0,
                        }}
                        defaultValue=""
                      >
                        <option value="">COLOR...</option>
                        {uniqueColors.map(color => (
                          <option key={color} value={color}>{color.toUpperCase()}</option>
                        ))}
                      </select>
                      <select
                        data-testid={`image-tag-angle-${index}`}
                        onChange={(e) => {
                          const angle = e.target.value;
                          const colorSel = document.querySelector(`[data-testid="image-tag-color-${index}"]`);
                          const color = colorSel ? colorSel.value : '';
                          if (color && angle) {
                            handleTagImage(imageUrl, color, angle);
                            e.target.value = '';
                            if (colorSel) colorSel.value = '';
                          }
                        }}
                        style={{
                          padding: '5px',
                          fontSize: '9px',
                          fontFamily: 'IBM Plex Mono, monospace',
                          border: '2px solid #000',
                          background: '#FFF',
                          color: '#2C2C2C',
                          cursor: 'pointer',
                          minWidth: 0,
                        }}
                        defaultValue=""
                      >
                        <option value="">ANGLE...</option>
                        <option value="front">FRONT</option>
                        <option value="back">BACK</option>
                        <option value="extra">EXTRA</option>
                      </select>
                    </div>
                    {/* Current tags on this image */}
                    {(() => {
                      const tags = tagsByImage.get(imageUrl) || [];
                      if (tags.length === 0) return null;
                      return (
                        <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {tags.map((t, ti) => {
                            const palette = t.angle === 'front'
                              ? { bg: '#4AE54A', fg: '#000' }
                              : t.angle === 'back'
                                ? { bg: '#FFC857', fg: '#000' }
                                : { bg: '#999', fg: '#FFF' };
                            return (
                              <button
                                key={ti}
                                type="button"
                                onClick={() => handleTagImage(imageUrl, t.color, 'none')}
                                data-testid={`image-tag-pill-${index}-${ti}`}
                                title="Click to remove"
                                style={{
                                  background: palette.bg,
                                  color: palette.fg,
                                  padding: '3px 6px',
                                  fontSize: '9px',
                                  border: '2px solid #000',
                                  fontWeight: 'bold',
                                  letterSpacing: '0.04em',
                                  cursor: 'pointer',
                                  fontFamily: 'IBM Plex Mono, monospace',
                                }}
                              >
                                {t.color.toUpperCase()} · {t.angle.toUpperCase()} ✕
                              </button>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Color Variant Overview */}
        <div style={{ background: '#FFF', padding: '20px', border: '3px solid #000', boxShadow: '4px 4px 0 0 #000' }}>
          <h2 style={{ fontSize: '12px', color: '#2C6E49', marginBottom: '15px', letterSpacing: '0.12em', fontWeight: 'bold' }}>
            [ COLOR VARIANTS ({uniqueColors.length}) ]
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '15px' }}>
            {uniqueColors.map(color => {
              const assignedImage = getColorImage(color);
              return (
                <div key={color} style={{ background: '#FEFEE3', border: '2px solid #000', padding: '10px' }}>
                  <p style={{ fontSize: '11px', color: '#2C2C2C', fontWeight: 'bold', marginBottom: '8px' }}>
                    {color.toUpperCase()}
                  </p>
                  
                  {assignedImage ? (
                    <img
                      src={assignedImage}
                      alt={color}
                      style={{
                        width: '100%',
                        height: '100px',
                        objectFit: 'cover',
                        border: '2px solid #4AE54A'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '100px',
                      background: '#E0E0E0',
                      border: '2px solid #999',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <p style={{ fontSize: '10px', color: '#666' }}>NO IMAGE</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default AdminProductEdit;
